import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
  TextField,
  InputAdornment,
  Pagination,
  Modal,
  IconButton,
  Grid,
  Chip,
  Checkbox,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Tooltip,
  Menu,
  MenuItem,
  Divider,
  Card,
  CardContent,
  Stack,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import api from '../services/api';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FilterListIcon from '@mui/icons-material/FilterList';
import DateRangeIcon from '@mui/icons-material/DateRange';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableChartIcon from '@mui/icons-material/TableChart';
import RefreshIcon from '@mui/icons-material/Refresh';
import UndoIcon from '@mui/icons-material/Undo';
import LoadingSpinner from '../components/LoadingSpinner';
import { ActivityTypes, logActivity } from '../utils/activityLogger';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { formatDistanceToNow } from 'date-fns';
import UserActionMenu from '../components/UserActionMenu';

const drawerWidth = 240;

const AllTransactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [filter, setFilter] = useState('');
  const [debouncedFilter, setDebouncedFilter] = useState('');
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalTransactions, setTotalTransactions] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [stats, setStats] = useState({ success: 0, credits: 0, debits: 0, totalVolume: 0 });
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // User modal functionality
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDialogOpen, setUserDialogOpen] = useState(false);

  // Date filtering
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [dateFilterOpen, setDateFilterOpen] = useState(false);

  // Export functionality
  const [exportAnchorEl, setExportAnchorEl] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);

  // Status and type filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [purposeFilter, setPurposeFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');

  // Auth context
  const { user } = useAuth();
  const isSuperAdmin = user?.type === 'superadmin';
  const { showToast } = useToast();

  // Bulk delete functionality
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Reversal functionality
  const [reversalDialogOpen, setReversalDialogOpen] = useState(false);
  const [selectedTransactionForReversal, setSelectedTransactionForReversal] = useState(null);
  const [reversalLoading, setReversalLoading] = useState(false);
  const [approveLoadingId, setApproveLoadingId] = useState(null);
  const API_BASE_URL = process.env.REACT_APP_API_URL;

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedFilter(filter);
    }, 600);
    return () => clearTimeout(timer);
  }, [filter]);

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const response = await api.dashboard.getAllTransactions({
        page,
        limit: rowsPerPage,
        search: debouncedFilter,
        status: statusFilter,
        trnxType: typeFilter,
        purpose: purposeFilter,
        startDate,
        endDate
      });

      const newTransactions = response.transactions || [];
      setTransactions(newTransactions);
      setTotalTransactions(response.pagination?.total || 0);
      setTotalPages(response.pagination?.pages || 0);
      if (response.stats) {
        setStats(response.stats);
      }

      // Log activity for viewing transactions
      await logActivity({
        action: ActivityTypes.VIEW,
        details: 'Viewed all transactions',
        metadata: {
          page: 'transactions',
          count: newTransactions.length,
          filters: { page, rowsPerPage, filter: debouncedFilter, statusFilter, typeFilter, purposeFilter }
        }
      });
    } catch (err) {
      setError(err.message || 'Error fetching transactions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, [page, rowsPerPage, statusFilter, typeFilter, purposeFilter, startDate, endDate, debouncedFilter]);

  // Handle search with debounce or manual refresh
  const handleSearch = () => {
    setPage(1);
    fetchTransactions();
  };

  // Helper function to determine transaction source
  const getTransactionSource = (transaction) => {
    const purpose = transaction.purpose?.toLowerCase() || '';
    const trnxType = transaction.trnxType?.toLowerCase() || '';
    const networkimg = transaction.networkimg?.toLowerCase() || '';
    const description = transaction.description?.toLowerCase() || '';

    // Internal transfers (Utilita to Utilita)
    if (networkimg === 'utilita' ||
      description.includes('internal transfer') ||
      description.includes('utilita user') ||
      (purpose.includes('transfer') && description.includes('utilita'))) {
      return 'internal';
    }

    // Bank transfers from Paystack
    if (purpose.includes('bank transfer')) {
      return 'paystack';
    }

    // Utilities (airtime, data, betting, electricity) from Payscribe
    if (purpose.includes('airtime') ||
      purpose.includes('data') ||
      purpose.includes('betting') ||
      purpose.includes('electricity') ||
      purpose.includes('cable') ||
      purpose.includes('tv')) {
      return 'payscribe';
    }

    // Safehaven transfers (external bank transfers, not internal)
    if (purpose.includes('transfer') &&
      !purpose.includes('bank transfer') &&
      !purpose.includes('transfer fee') &&
      !purpose.includes('electronic levy') &&
      networkimg !== 'utilita' &&
      !description.includes('internal transfer') &&
      !description.includes('utilita user')) {
      return 'safehaven';
    }

    // Default source
    return 'other';
  };

  // Helper function to normalize status
  const normalizeStatus = (status, source) => {
    const statusLower = status?.toLowerCase() || '';
    const sourceLower = source?.toLowerCase() || '';

    // If it's Paystack and has any processing status, normalize to success
    if (sourceLower === 'paystack' && (
      statusLower === 'completed' ||
      statusLower === 'success' ||
      statusLower.includes('processing') ||
      statusLower.includes('processed')
    )) {
      return 'success';
    }

    // Normalize other success-like statuses
    if (statusLower === 'completed' || statusLower === 'success') {
      return 'success';
    }

    // Return original status for everything else
    return status || 'unknown';
  };

  // Helper function to format amounts
  const formatAmount = (amount) => {
    if (amount === undefined || amount === null) return '₦0.00';
    return `₦${Number(amount).toLocaleString()}`;
  };

  // Client-side filtering removed - now using server-side results directly
  const paginatedTransactions = transactions;
  const pageCount = totalPages;

  // Bulk selection handlers
  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const allIds = paginatedTransactions.map(tx => tx._id || tx.id);
      setSelectedIds(allIds);
      setSelectAll(true);
    } else {
      setSelectedIds([]);
      setSelectAll(false);
    }
  };

  const handleSelectTransaction = (transactionId) => {
    const newSelectedIds = selectedIds.includes(transactionId)
      ? selectedIds.filter(id => id !== transactionId)
      : [...selectedIds, transactionId];

    setSelectedIds(newSelectedIds);
    setSelectAll(newSelectedIds.length === paginatedTransactions.length);
  };

  // Delete handlers
  const handleDeleteSelected = async () => {
    setDeleteLoading(true);
    try {
      // Call API to delete transactions
      await api.dashboard.deleteTransactions(selectedIds);

      // Remove deleted transactions from state
      setTransactions(prev => prev.filter(tx => !selectedIds.includes(tx._id || tx.id)));

      // Reset selection
      setSelectedIds([]);
      setSelectAll(false);
      setDeleteDialogOpen(false);

      // Show success message
      showToast(`Successfully deleted ${selectedIds.length} transaction${selectedIds.length > 1 ? 's' : ''}`, 'success');
      setSuccessMessage(`Successfully deleted ${selectedIds.length} transaction${selectedIds.length > 1 ? 's' : ''}`);

      // Log activity
      await logActivity({
        action: ActivityTypes.DELETE,
        details: `Deleted ${selectedIds.length} transaction${selectedIds.length > 1 ? 's' : ''}`,
        metadata: {
          deletedIds: selectedIds,
          count: selectedIds.length
        }
      });

      // Clear success message after 5 seconds
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err) {
      showToast(err.message || 'Error deleting transactions', 'error');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleSingleDelete = async (transactionId) => {
    try {
      await api.dashboard.deleteTransactions([transactionId]);

      // Remove deleted transaction from state
      setTransactions(prev => prev.filter(tx => (tx._id || tx.id) !== transactionId));

      // Show success message
      showToast('Transaction deleted successfully', 'success');
      setSuccessMessage('Transaction deleted successfully');

      // Log activity
      await logActivity({
        action: ActivityTypes.DELETE,
        details: 'Deleted single transaction',
        metadata: {
          deletedId: transactionId
        }
      });

      // Clear success message after 5 seconds
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err) {
      showToast(err.message || 'Error deleting transaction', 'error');
    }
  };

  const handleTransactionClick = (transaction) => {
    setSelectedTransaction(transaction);
    setIsModalOpen(true);
    // Log activity for viewing transaction details
    logActivity({
      action: ActivityTypes.VIEW,
      details: `Viewed transaction details: ${transaction.reference || transaction._id}`,
      metadata: {
        transactionId: transaction._id,
        transactionType: transaction.type,
        amount: transaction.amount,
        status: transaction.status
      }
    });
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedTransaction(null);
  };

  // User modal handlers
  const handleUserClick = async (userData) => {
    try {
      // Fetch complete user data using the new API
      let userDetails = userData;
      if (userData._id) {
        try {
          // Fetch complete user details from API
          const response = await api.dashboard.getUserById(userData._id);
          userDetails = response;
        } catch (apiError) {
          console.warn('Could not fetch complete user details, using basic info:', apiError);
          // Fallback to basic user data if API call fails
          userDetails = {
            _id: userData._id,
            fullname: userData.fullname,
            username: userData.username,
            email: userData.email,
            // Add placeholder values for other fields
            phone: '-',
            gender: '-',
            balance: 0,
            role: 'user',
            status: 'active',
            createdAt: new Date(),
            nin: '-',
            bvn: '-',
            isKycVerified: false,
            isKycninVerified: false,
            isKycbvnVerified: false,
            virtuaAccountBank: '-',
            virtuaAccountName: '-',
            virtuaAccountNo: '-'
          };
        }
      }

      setSelectedUser(userDetails);
      setUserDialogOpen(true);

      // Log activity
      await logActivity({
        action: ActivityTypes.VIEW,
        details: `Viewed user details: ${userDetails.fullname || userDetails.name || userDetails.username}`,
        metadata: {
          page: 'transactions',
          userId: userDetails._id,
          userName: userDetails.fullname || userDetails.name || userDetails.username
        }
      });
    } catch (err) {
      showToast('Error opening user details', 'error');
    }
  };

  const handleUserDialogClose = () => {
    setUserDialogOpen(false);
    setSelectedUser(null);
  };

  // Export handlers
  const handleExportClick = (event) => {
    setExportAnchorEl(event.currentTarget);
  };

  const handleExportClose = () => {
    setExportAnchorEl(null);
  };

  const exportToExcel = async () => {
    setExportLoading(true);
    try {
      const data = transactions.map(tx => ({
        'Reference ID': tx.reference || tx._id || tx.id,
        'User Name': tx.userName || tx.user || '-',
        'Username': tx.username || '-',
        'Amount': tx.amount !== undefined ? `₦${Number(tx.amount).toLocaleString()}` : '-',
        'Balance Before': tx.balanceBefore !== undefined ? `₦${Number(tx.balanceBefore).toLocaleString()}` : '-',
        'Balance After': tx.balanceAfter !== undefined ? `₦${Number(tx.balanceAfter).toLocaleString()}` : '-',
        'Type': tx.type || '-',
        'Transaction Type': tx.trnxType || '-',
        'Purpose': tx.purpose || '-',
        'Source': getTransactionSource(tx) || '-',
        'Network': tx.network || '-',
        'Status': tx.status || '-',
        'Date': tx.createdAt ? new Date(tx.createdAt).toLocaleString() : '-',
        'Description': tx.description || '-'
      }));

      // Create CSV content
      const headers = Object.keys(data[0]);
      const csvContent = [
        headers.join(','),
        ...data.map(row => headers.map(header => `"${row[header]}"`).join(','))
      ].join('\n');

      // Download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `transactions_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      showToast('Transactions exported to Excel successfully', 'success');

      // Log activity
      await logActivity({
        action: ActivityTypes.EXPORT,
        details: `Exported ${transactions.length} transactions to Excel`,
        metadata: {
          format: 'excel',
          count: transactions.length,
          filters: { startDate, endDate, statusFilter, typeFilter, purposeFilter, sourceFilter }
        }
      });
    } catch (err) {
      showToast('Error exporting to Excel', 'error');
    } finally {
      setExportLoading(false);
      handleExportClose();
    }
  };

  const exportToPDF = async () => {
    setExportLoading(true);
    try {
      // Import jsPDF dynamically
      const { jsPDF } = await import('jspdf');
      const pdf = new jsPDF();

      // Add title
      pdf.setFontSize(18);
      pdf.text('Transactions Report', 20, 20);
      pdf.setFontSize(12);
      pdf.text(`Generated on: ${new Date().toLocaleString()}`, 20, 30);
      pdf.text(`Total Records: ${transactions.length}`, 20, 40);

      // Add filters info
      if (startDate || endDate || statusFilter !== 'all' || typeFilter !== 'all' || purposeFilter !== 'all') {
        pdf.text('Filters Applied:', 20, 55);
        let filterYPos = 65;
        if (startDate) {
          pdf.text(`Start Date: ${startDate}`, 25, filterYPos);
          filterYPos += 5;
        }
        if (endDate) {
          pdf.text(`End Date: ${endDate}`, 25, filterYPos);
          filterYPos += 5;
        }
        if (statusFilter !== 'all') {
          pdf.text(`Status: ${statusFilter}`, 25, filterYPos);
          filterYPos += 5;
        }
        if (typeFilter !== 'all') {
          pdf.text(`Type: ${typeFilter}`, 25, filterYPos);
          filterYPos += 5;
        }
        if (purposeFilter !== 'all') {
          pdf.text(`Purpose: ${purposeFilter}`, 25, filterYPos);
          filterYPos += 5;
        }
      }

      // Add table headers
      const headers = ['Ref ID', 'User', 'Amount', 'Type', 'Source', 'Status', 'Date'];
      const columnWidths = [30, 35, 25, 20, 25, 25, 35];
      let xPos = 20;
      let tableYPos = 90;

      pdf.setFontSize(10);
      pdf.setFont(undefined, 'bold');
      headers.forEach((header, index) => {
        pdf.text(header, xPos, tableYPos);
        xPos += columnWidths[index];
      });

      // Add table data
      pdf.setFont(undefined, 'normal');
      tableYPos += 10;
      let pageCount = 1;

      transactions.forEach((tx, index) => {
        if (tableYPos > 270) {
          pdf.addPage();
          tableYPos = 20;
          pageCount++;
        }

        xPos = 20;
        const rowData = [
          (tx.reference || tx._id || tx.id).toString().substring(0, 8),
          (tx.userName || tx.user || '-').toString().substring(0, 15),
          `₦${Number(tx.amount || 0).toLocaleString()}`,
          tx.type || '-',
          getTransactionSource(tx),
          tx.status || '-',
          tx.createdAt ? new Date(tx.createdAt).toLocaleDateString() : '-'
        ];

        rowData.forEach((cell, cellIndex) => {
          pdf.text(cell.toString(), xPos, tableYPos);
          xPos += columnWidths[cellIndex];
        });

        tableYPos += 7;
      });

      // Save PDF
      pdf.save(`transactions_${new Date().toISOString().split('T')[0]}.pdf`);

      showToast('Transactions exported to PDF successfully', 'success');

      // Log activity
      await logActivity({
        action: ActivityTypes.EXPORT,
        details: `Exported ${transactions.length} transactions to PDF`,
        metadata: {
          format: 'pdf',
          count: transactions.length,
          filters: { startDate, endDate, statusFilter, typeFilter, purposeFilter }
        }
      });
    } catch (err) {
      showToast('Error exporting to PDF', 'error');
    } finally {
      setExportLoading(false);
      handleExportClose();
    }
  };

  const clearFilters = () => {
    setFilter('');
    setStatusFilter('all');
    setTypeFilter('all');
    setPurposeFilter('all');
    setSourceFilter('all');
    setStartDate('');
    setEndDate('');
    setPage(1);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1); // Reset to first page when changing rows per page
  };

  const TransactionModal = ({ transaction, open, onClose }) => {
    if (!transaction) return null;

    const getStatusColor = (status) => {
      const normalizedStatus = normalizeStatus(status, getTransactionSource(transaction));
      const statusLower = normalizedStatus?.toLowerCase();
      if (statusLower === 'success') return '#22c55e';
      if (statusLower === 'failed' || statusLower === 'total failed') return '#ef4444';
      if (statusLower === 'pending') return '#f59e0b';
      if (statusLower === 'reversal') return '#8b5cf6';
      return '#6b7280';
    };

    const getTypeColor = (type) => {
      const typeLower = type?.toLowerCase();
      if (typeLower === 'credit') return '#22c55e';
      if (typeLower === 'debit') return '#ef4444';
      return '#6b7280';
    };

    const formatAmount = (amount) => {
      if (amount === undefined || amount === null) return '₦0.00';
      return `₦${Number(amount).toLocaleString()}`;
    };

    const formatDate = (dateString) => {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return {
        full: date.toLocaleString(),
        relative: formatDistanceToNow(date, { addSuffix: true })
      };
    };

    const dateInfo = formatDate(transaction.createdAt);

    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            boxShadow: theme.palette.mode === 'dark'
              ? `0 25px 80px ${alpha(theme.palette.primary.main, 0.6)}`
              : `0 25px 80px ${alpha(theme.palette.primary.main, 0.4)}`,
            background: theme.palette.mode === 'dark'
              ? `linear-gradient(145deg, #0f172a 0%, #1e293b 100%)`
              : `linear-gradient(145deg, #ffffff 0%, #f0f2f5 100%)`,
            border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            maxHeight: '90vh',
            overflow: 'hidden',
            position: 'relative',
          },
        }}
      >
        {/* Animated Floating Elements */}
        <Box
          sx={{
            position: 'absolute',
            top: 20,
            right: 20,
            width: 60,
            height: 60,
            borderRadius: '50%',
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
            animation: 'float 6s ease-in-out infinite',
            zIndex: 0,
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: 40,
            left: 30,
            width: 40,
            height: 40,
            borderRadius: '50%',
            background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.1)} 100%)`,
            animation: 'float 8s ease-in-out infinite reverse',
            zIndex: 0,
          }}
        />

        {/* Enhanced Header */}
        <Box
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
            color: 'white',
            p: 2,
            position: 'relative',
            overflow: 'visible',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.05"%3E%3Ccircle cx="30" cy="30" r="2"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
              animation: 'slide 20s linear infinite',
            },
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, position: 'relative', zIndex: 1 }}>
            <Box>
              <Typography variant="h5" fontWeight={800} sx={{ mb: 1, textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
                Transaction Details
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9, fontWeight: 500 }}>
                {transaction.reference || transaction._id || transaction.id}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.7, mt: 0.5 }}>
                {dateInfo.relative}
              </Typography>
            </Box>
            <IconButton
              onClick={onClose}
              sx={{
                color: 'white',
                bgcolor: 'rgba(255,255,255,0.15)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255,255,255,0.2)',
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.25)',
                  transform: 'scale(1.1)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Enhanced Summary Cards */}
          <Grid container spacing={2} sx={{ position: 'relative', zIndex: 1 }}>
            <Grid item xs={12} sm={4}>
              <Card sx={{
                p: 2.5,
                borderRadius: 4,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                textAlign: 'center',
                transition: 'all 0.4s ease',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: -50,
                  right: -50,
                  width: 100,
                  height: 100,
                  borderRadius: '50%',
                  background: 'rgba(255,255,255,0.1)',
                  animation: 'pulse 3s ease-in-out infinite',
                },
                '&:hover': {
                  transform: 'translateY(-8px) scale(1.02)',
                  boxShadow: '0 20px 40px rgba(102, 126, 234, 0.4)',
                },
              }}>
                <Box sx={{ position: 'relative', zIndex: 1 }}>
                  <Typography variant="h2" fontWeight={900} sx={{
                    mb: 1,
                    textShadow: '0 2px 8px rgba(0,0,0,0.3)',
                    background: 'linear-gradient(45deg, #ffffff 30%, #f0f0f0 90%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}>
                    {formatAmount(transaction.amount)}
                  </Typography>
                  <Chip
                    label={transaction.type || 'Unknown'}
                    size="medium"
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.2)',
                      color: 'white',
                      fontWeight: 700,
                      fontSize: '0.9rem',
                      px: 2,
                      py: 1,
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255,255,255,0.3)',
                      '&:hover': {
                        bgcolor: 'rgba(255,255,255,0.3)',
                      },
                    }}
                  />
                  <Typography variant="body2" sx={{ mt: 1, opacity: 0.9, fontWeight: 500 }}>
                    Transaction Amount
                  </Typography>
                </Box>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card sx={{
                p: 2.5,
                borderRadius: 4,
                background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                color: 'white',
                textAlign: 'center',
                transition: 'all 0.4s ease',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: -30,
                  left: -30,
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  background: 'rgba(255,255,255,0.1)',
                  animation: 'float 4s ease-in-out infinite',
                },
                '&:hover': {
                  transform: 'translateY(-8px) scale(1.02)',
                  boxShadow: '0 20px 40px rgba(240, 147, 251, 0.4)',
                },
              }}>
                <Box sx={{ position: 'relative', zIndex: 1 }}>
                  <Chip
                    label={normalizeStatus(transaction.status, getTransactionSource(transaction))}
                    size="large"
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.2)',
                      color: 'white',
                      fontWeight: 800,
                      fontSize: '1rem',
                      px: 3,
                      py: 1.5,
                      mb: 2,
                      backdropFilter: 'blur(10px)',
                      border: '2px solid rgba(255,255,255,0.3)',
                      boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
                      '&:hover': {
                        bgcolor: 'rgba(255,255,255,0.3)',
                        transform: 'scale(1.05)',
                      },
                      transition: 'all 0.3s ease',
                    }}
                  />
                  <Typography variant="h6" fontWeight={700} sx={{
                    opacity: 0.95,
                    textShadow: '0 1px 3px rgba(0,0,0,0.3)',
                  }}>
                    Status
                  </Typography>
                  <Box sx={{
                    mt: 1,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: 0.5
                  }}>
                    <Box sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: getStatusColor(transaction.status),
                      animation: 'pulse 2s ease-in-out infinite',
                    }} />
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>
                      {normalizeStatus(transaction.status, getTransactionSource(transaction)) === 'success' ? 'Completed' :
                        normalizeStatus(transaction.status, getTransactionSource(transaction)) === 'failed' ? 'Failed' :
                          normalizeStatus(transaction.status, getTransactionSource(transaction)) === 'pending' ? 'Processing' : 'Active'}
                    </Typography>
                  </Box>
                </Box>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card sx={{
                p: 2.5,
                borderRadius: 4,
                background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                color: 'white',
                textAlign: 'center',
                transition: 'all 0.4s ease',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  bottom: -40,
                  right: -40,
                  width: 90,
                  height: 90,
                  borderRadius: '50%',
                  background: 'rgba(255,255,255,0.1)',
                  animation: 'float 5s ease-in-out infinite reverse',
                },
                '&:hover': {
                  transform: 'translateY(-8px) scale(1.02)',
                  boxShadow: '0 20px 40px rgba(79, 172, 254, 0.4)',
                },
              }}>
                <Box sx={{ position: 'relative', zIndex: 1 }}>
                  <Box sx={{
                    width: 60,
                    height: 60,
                    borderRadius: '50%',
                    background: 'rgba(255,255,255,0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 1rem',
                    backdropFilter: 'blur(10px)',
                    border: '2px solid rgba(255,255,255,0.3)',
                    boxShadow: '0 8px 25px rgba(0,0,0,0.2)',
                  }}>
                    <Typography variant="h4" fontWeight={800} sx={{
                      textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                    }}>
                      {transaction.userName ? transaction.userName.charAt(0).toUpperCase() : 'U'}
                    </Typography>
                  </Box>
                  <Typography variant="h6" fontWeight={700} sx={{
                    mb: 0.5,
                    textShadow: '0 1px 3px rgba(0,0,0,0.3)',
                    lineHeight: 1.2,
                  }}>
                    {transaction.userName || transaction.user || 'Unknown User'}
                  </Typography>
                  {transaction.username && (
                    <Typography variant="body2" sx={{
                      opacity: 0.9,
                      fontWeight: 500,
                      fontStyle: 'italic',
                    }}>
                      @{transaction.username}
                    </Typography>
                  )}
                  <Typography variant="caption" sx={{
                    opacity: 0.8,
                    mt: 1,
                    display: 'block',
                    fontWeight: 500,
                  }}>
                    Account Holder
                  </Typography>
                </Box>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* Enhanced Content */}
        <Box sx={{ p: 2, maxHeight: 'calc(90vh - 300px)', overflow: 'auto' }}>
          <Grid container spacing={3}>
            {/* Transaction Information */}
            <Grid item xs={12}>
              <Card sx={{
                p: 2.5,
                borderRadius: 3,
                mb: 2.5,
                background: theme.palette.mode === 'dark'
                  ? 'rgba(30, 41, 59, 0.8)'
                  : 'rgba(255,255,255,0.8)',
                backdropFilter: 'blur(10px)',
                border: theme.palette.mode === 'dark'
                  ? '1px solid rgba(255,255,255,0.1)'
                  : '1px solid rgba(255,255,255,0.3)',
                boxShadow: theme.palette.mode === 'dark'
                  ? '0 8px 32px rgba(0,0,0,0.3)'
                  : '0 8px 32px rgba(0,0,0,0.1)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: theme.palette.mode === 'dark'
                    ? '0 12px 40px rgba(0,0,0,0.4)'
                    : '0 12px 40px rgba(0,0,0,0.15)',
                },
              }}>
                <Typography variant="h6" fontWeight={700} gutterBottom color="primary" sx={{ mb: 2.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 4, height: 18, bgcolor: 'primary.main', borderRadius: 1 }} />
                  Transaction Information
                </Typography>
                <Grid container spacing={2.5}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Transaction Type
                    </Typography>
                    <Typography variant="body1" fontWeight={700} sx={{ mt: 0.5 }}>
                      {transaction.trnxType || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Purpose
                    </Typography>
                    <Typography variant="body1" fontWeight={700} sx={{ mt: 0.5 }}>
                      {transaction.purpose || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Source
                    </Typography>
                    <Typography variant="body1" fontWeight={700} sx={{ mt: 0.5 }}>
                      {getTransactionSource(transaction)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Network
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.5 }}>
                      {transaction.networkimg && (
                        <img
                          src={`/images/${transaction.networkimg.toLowerCase()}.png`}
                          alt={transaction.networkimg}
                          style={{
                            width: 28,
                            height: 28,
                            objectFit: 'contain',
                            borderRadius: '6px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                          }}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.style.display = 'none';
                          }}
                        />
                      )}
                      <Typography variant="body1" fontWeight={700}>
                        {transaction.network || 'N/A'}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Card>
            </Grid>

            {/* Financial Details */}
            <Grid item xs={12}>
              <Card sx={{
                p: 2.5,
                borderRadius: 3,
                mb: 2.5,
                background: theme.palette.mode === 'dark'
                  ? 'rgba(30, 41, 59, 0.8)'
                  : 'rgba(255,255,255,0.8)',
                backdropFilter: 'blur(10px)',
                border: theme.palette.mode === 'dark'
                  ? '1px solid rgba(255,255,255,0.1)'
                  : '1px solid rgba(255,255,255,0.3)',
                boxShadow: theme.palette.mode === 'dark'
                  ? '0 8px 32px rgba(0,0,0,0.3)'
                  : '0 8px 32px rgba(0,0,0,0.1)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: theme.palette.mode === 'dark'
                    ? '0 12px 40px rgba(0,0,0,0.4)'
                    : '0 12px 40px rgba(0,0,0,0.15)',
                },
              }}>
                <Typography variant="h6" fontWeight={700} gutterBottom color="primary" sx={{ mb: 2.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 4, height: 18, bgcolor: 'primary.main', borderRadius: 1 }} />
                  Financial Details
                </Typography>
                <Grid container spacing={2.5}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Balance Before
                    </Typography>
                    <Typography variant="body1" fontWeight={700} color="text.primary" sx={{ mt: 0.5 }}>
                      {formatAmount(transaction.balanceBefore)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Balance After
                    </Typography>
                    <Typography variant="body1" fontWeight={700} color="text.primary" sx={{ mt: 0.5 }}>
                      {formatAmount(transaction.balanceAfter)}
                    </Typography>
                  </Grid>
                  {transaction.balanceBefore !== undefined && transaction.balanceAfter !== undefined && (
                    <Grid item xs={12}>
                      <Box sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        textAlign: 'center',
                        mt: 1.5,
                        border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                      }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Balance Change
                        </Typography>
                        <Typography variant="h5" fontWeight={800} color="primary" sx={{ mt: 0.5 }}>
                          {Number(transaction.balanceAfter) - Number(transaction.balanceBefore) >= 0 ? '+' : ''}
                          {formatAmount(Number(transaction.balanceAfter) - Number(transaction.balanceBefore))}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </Card>
            </Grid>

            {/* Description */}
            {transaction.description && (
              <Grid item xs={12}>
                <Card sx={{
                  p: 2.5,
                  borderRadius: 3,
                  mb: 2.5,
                  background: theme.palette.mode === 'dark'
                    ? 'rgba(30, 41, 59, 0.8)'
                    : 'rgba(255,255,255,0.8)',
                  backdropFilter: 'blur(10px)',
                  border: theme.palette.mode === 'dark'
                    ? '1px solid rgba(255,255,255,0.1)'
                    : '1px solid rgba(255,255,255,0.3)',
                  boxShadow: theme.palette.mode === 'dark'
                    ? '0 8px 32px rgba(0,0,0,0.3)'
                    : '0 8px 32px rgba(0,0,0,0.1)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: theme.palette.mode === 'dark'
                      ? '0 12px 40px rgba(0,0,0,0.4)'
                      : '0 12px 40px rgba(0,0,0,0.15)',
                  },
                }}>
                  <Typography variant="h6" fontWeight={700} gutterBottom color="primary" sx={{ mb: 2.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 4, height: 18, bgcolor: 'primary.main', borderRadius: 1 }} />
                    Description
                  </Typography>
                  <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                    {transaction.description}
                  </Typography>
                </Card>
              </Grid>
            )}

            {/* Additional Information */}
            <Grid item xs={12}>
              <Card sx={{
                p: 2.5,
                borderRadius: 3,
                background: theme.palette.mode === 'dark'
                  ? 'rgba(30, 41, 59, 0.8)'
                  : 'rgba(255,255,255,0.8)',
                backdropFilter: 'blur(10px)',
                border: theme.palette.mode === 'dark'
                  ? '1px solid rgba(255,255,255,0.1)'
                  : '1px solid rgba(255,255,255,0.3)',
                boxShadow: theme.palette.mode === 'dark'
                  ? '0 8px 32px rgba(0,0,0,0.3)'
                  : '0 8px 32px rgba(0,0,0,0.1)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: theme.palette.mode === 'dark'
                    ? '0 12px 40px rgba(0,0,0,0.4)'
                    : '0 12px 40px rgba(0,0,0,0.15)',
                },
              }}>
                <Typography variant="h6" fontWeight={700} gutterBottom color="primary" sx={{ mb: 2.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 4, height: 18, bgcolor: 'primary.main', borderRadius: 1 }} />
                  Additional Information
                </Typography>
                <Grid container spacing={2.5}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Created Date
                    </Typography>
                    <Typography variant="body1" fontWeight={700} sx={{ mt: 0.5 }}>
                      {dateInfo.full}
                    </Typography>
                  </Grid>
                  {transaction.fullNameTransactionEntity && (
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Entity
                      </Typography>
                      <Typography variant="body1" fontWeight={700} sx={{ mt: 0.5 }}>
                        {transaction.fullNameTransactionEntity}
                      </Typography>
                    </Grid>
                  )}
                  {transaction.metadata && Object.keys(transaction.metadata).length > 0 && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Metadata
                      </Typography>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 1.5,
                          mt: 1,
                          borderRadius: 2,
                          maxHeight: '100px',
                          overflow: 'auto',
                          bgcolor: 'background.default',
                          border: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                        }}
                      >
                        <pre style={{
                          margin: 0,
                          fontSize: '0.75rem',
                          fontFamily: 'monospace',
                          lineHeight: 1.4,
                        }}>
                          {JSON.stringify(transaction.metadata, null, 2)}
                        </pre>
                      </Paper>
                    </Grid>
                  )}
                </Grid>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* Enhanced Footer */}
        <Box sx={{
          p: 2,
          borderTop: `2px solid ${alpha(theme.palette.divider, 0.2)}`,
          background: theme.palette.mode === 'dark'
            ? 'rgba(30, 41, 59, 0.5)'
            : 'rgba(255,255,255,0.5)',
          backdropFilter: 'blur(10px)',
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button
              onClick={onClose}
              variant="contained"
              size="large"
              sx={{
                borderRadius: 3,
                textTransform: 'none',
                px: 4,
                py: 1.5,
                fontWeight: 700,
                fontSize: '1.1rem',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                boxShadow: '0 8px 25px rgba(102, 126, 234, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 12px 35px rgba(102, 126, 234, 0.4)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              Close
            </Button>
          </Box>
        </Box>

        <style>
          {`
            @keyframes float {
              0%, 100% { transform: translateY(0px); }
              50% { transform: translateY(-20px); }
            }
            @keyframes slide {
              0% { transform: translateX(-100%); }
              100% { transform: translateX(100%); }
            }
          `}
        </style>
      </Dialog>
    );
  };

  // Helper function to check if transaction is eligible for reversal
  const isEligibleForReversal = (transaction) => {
    if (!transaction || !transaction.createdAt) return false;

    const status = normalizeStatus(transaction.status, getTransactionSource(transaction));
    if (status.toLowerCase() !== 'pending') return false;

    const transactionTime = new Date(transaction.createdAt);
    const currentTime = new Date();
    const timeDifference = currentTime - transactionTime;
    const fiveMinutesInMs = 5 * 60 * 1000; // 5 minutes in milliseconds

    return timeDifference > fiveMinutesInMs;
  };

  // Reversal handlers
  const handleReversalClick = (transaction) => {
    setSelectedTransactionForReversal(transaction);
    setReversalDialogOpen(true);
  };

  const handleReversalConfirm = async () => {
    if (!selectedTransactionForReversal) return;

    setReversalLoading(true);
    try {
      // Call API to reverse the transaction
      await api.dashboard.reverseTransaction(selectedTransactionForReversal._id || selectedTransactionForReversal.id);

      // Update the transaction status in state
      setTransactions(prev => prev.map(tx =>
        (tx._id || tx.id) === (selectedTransactionForReversal._id || selectedTransactionForReversal.id)
          ? { ...tx, status: 'failed' }
          : tx
      ));

      // Close dialog and show success message
      setReversalDialogOpen(false);
      setSelectedTransactionForReversal(null);
      showToast('Transaction reversed successfully', 'success');

      // Log activity
      await logActivity({
        action: ActivityTypes.UPDATE,
        details: `Reversed transaction: ${selectedTransactionForReversal.reference || selectedTransactionForReversal._id}`,
        metadata: {
          transactionId: selectedTransactionForReversal._id || selectedTransactionForReversal.id,
          originalStatus: selectedTransactionForReversal.status,
          newStatus: 'failed'
        }
      });
    } catch (err) {
      showToast(err.message || 'Error reversing transaction', 'error');
    } finally {
      setReversalLoading(false);
    }
  };

  const handleReversalCancel = () => {
    setReversalDialogOpen(false);
    setSelectedTransactionForReversal(null);
  };

  const handleApproveRefund = async (transaction) => {
    try {
      setApproveLoadingId(transaction._id || transaction.id);
      const reference = transaction.reference || transaction._id || transaction.id;
      // Try calling backend endpoint directly with fetch (uses same origin)
      const token = localStorage.getItem('token') || localStorage.getItem('authToken');
      const res = await fetch(`${API_BASE_URL}/admin/transactions/${reference}/approve-reversal`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'x-auth-token': token } : {})
        },
      });
      const data = await res.json();
      if (!res.ok || data.success === false) {
        throw new Error(data.message || 'Failed to approve refund');
      }

      showToast('Refund approved and processed', 'success');

      // Update UI state: mark tx as failed and clear pending
      setTransactions(prev => prev.map(tx => {
        if ((tx.reference || tx._id || tx.id) === reference) {
          return { ...tx, status: 'failed' };
        }
        return tx;
      }));
    } catch (err) {
      showToast(err.message || 'Error approving refund', 'error');
    } finally {
      setApproveLoadingId(null);
    }
  };

  return (
    <Box sx={{
      background: theme.palette.mode === 'dark'
        ? 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)'
        : 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      minHeight: '100vh',
      p: { xs: 1, sm: 2 }
    }}>
      {/* Header Section */}
      <Box sx={{ mb: { xs: 2, sm: 4 } }}>
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          mb: { xs: 2, sm: 3 },
          gap: 2,
          background: theme.palette.mode === 'dark'
            ? 'rgba(30, 41, 59, 0.9)'
            : 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(10px)',
          borderRadius: 3,
          p: { xs: 1.5, sm: 2 },
          boxShadow: theme.palette.mode === 'dark'
            ? '0 8px 32px rgba(0,0,0,0.3)'
            : '0 8px 32px rgba(0,0,0,0.1)',
          border: theme.palette.mode === 'dark'
            ? '1px solid rgba(255, 255, 255, 0.1)'
            : '1px solid rgba(255, 255, 255, 0.2)'
        }}>
          <Box sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
            <Typography
              variant="h4"
              fontWeight={700}
              sx={{
                background: 'linear-gradient(135deg, #A80A0A 0%, #FF6B6B 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                textShadow: '0 2px 4px rgba(0,0,0,0.1)',
                mb: 1,
                fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' }
              }}
            >
              All Transactions
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{
              opacity: 0.8,
              fontSize: { xs: '0.875rem', sm: '1rem' }
            }}>
              Manage and monitor all system transactions with advanced filtering and export capabilities
            </Typography>
          </Box>

          {/* Export and Actions */}
          <Box sx={{
            display: 'flex',
            gap: 1,
            flexWrap: 'wrap',
            justifyContent: { xs: 'center', sm: 'flex-end' }
          }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => window.location.reload()}
              size={isMobile ? "small" : "medium"}
              sx={{
                borderRadius: 3,
                textTransform: 'none',
                fontWeight: 600,
                borderColor: alpha(theme.palette.primary.main, 0.3),
                background: 'rgba(255, 255, 255, 0.8)',
                backdropFilter: 'blur(10px)',
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                '&:hover': {
                  borderColor: theme.palette.primary.main,
                  backgroundColor: alpha(theme.palette.primary.main, 0.05),
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              Refresh
            </Button>

            <Button
              variant="contained"
              startIcon={<FileDownloadIcon />}
              onClick={handleExportClick}
              disabled={exportLoading}
              size={isMobile ? "small" : "medium"}
              sx={{
                borderRadius: 3,
                textTransform: 'none',
                fontWeight: 600,
                background: 'linear-gradient(135deg, #A80A0A 0%, #FF6B6B 100%)',
                boxShadow: '0 4px 15px rgba(168, 10, 10, 0.3)',
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                '&:hover': {
                  background: 'linear-gradient(135deg, #8B0000 0%, #E55A5A 100%)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 25px rgba(168, 10, 10, 0.4)',
                },
                '&:disabled': {
                  background: 'linear-gradient(135deg, #ccc 0%, #ddd 100%)',
                  transform: 'none',
                  boxShadow: 'none',
                },
                transition: 'all 0.3s ease',
              }}
            >
              {exportLoading ? 'Exporting...' : 'Export'}
            </Button>
          </Box>
        </Box>

        {/* Enhanced Stats Cards */}
        <Grid container spacing={{ xs: 1, sm: 2, md: 3 }} sx={{ mb: { xs: 2, sm: 4 } }}>
          <Grid item xs={6} sm={6} md={3}>
            <Card sx={{
              borderRadius: 3,
              boxShadow: theme.palette.mode === 'dark' ? '0 8px 32px rgba(0,0,0,0.3)' : '0 8px 32px rgba(0,0,0,0.1)',
              background: theme.palette.mode === 'dark'
                ? 'linear-gradient(135deg, #1e293b 0%, #334155 100%)'
                : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              position: 'relative',
              overflow: 'hidden',
              '&:hover': {
                transform: 'translateY(-5px)',
                boxShadow: theme.palette.mode === 'dark' ? '0 15px 45px rgba(0,0,0,0.4)' : '0 15px 45px rgba(0,0,0,0.2)',
              },
              transition: 'all 0.3s ease',
            }}>
              <Box sx={{
                position: 'absolute',
                top: -20,
                right: -20,
                width: 100,
                height: 100,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.1)',
                backdropFilter: 'blur(10px)',
              }} />
              <CardContent sx={{ p: { xs: 1, sm: 2 }, position: 'relative', zIndex: 1 }}>
                <Typography variant="h4" fontWeight={700} sx={{
                  mb: 1,
                  fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' }
                }}>
                  {totalTransactions.toLocaleString()}
                </Typography>
                <Typography variant="body2" sx={{
                  opacity: 0.9,
                  fontWeight: 500,
                  fontSize: { xs: '0.7rem', sm: '0.8rem' }
                }}>
                  Total Transactions
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <Card sx={{
              borderRadius: 3,
              boxShadow: theme.palette.mode === 'dark' ? '0 8px 32px rgba(0,0,0,0.3)' : '0 8px 32px rgba(0,0,0,0.1)',
              background: theme.palette.mode === 'dark'
                ? 'linear-gradient(135deg, #374151 0%, #4b5563 100%)'
                : 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              color: 'white',
              position: 'relative',
              overflow: 'hidden',
              '&:hover': {
                transform: 'translateY(-5px)',
                boxShadow: theme.palette.mode === 'dark' ? '0 15px 45px rgba(0,0,0,0.4)' : '0 15px 45px rgba(0,0,0,0.2)',
              },
              transition: 'all 0.3s ease',
            }}>
              <Box sx={{
                position: 'absolute',
                top: -20,
                right: -20,
                width: 100,
                height: 100,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.1)',
                backdropFilter: 'blur(10px)',
              }} />
              <CardContent sx={{ p: { xs: 1, sm: 2 }, position: 'relative', zIndex: 1 }}>
                <Typography variant="h4" fontWeight={700} sx={{
                  mb: 1,
                  fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' }
                }}>
                  {stats.success.toLocaleString()}
                </Typography>
                <Typography variant="body2" sx={{
                  opacity: 0.9,
                  fontWeight: 500,
                  fontSize: { xs: '0.7rem', sm: '0.8rem' }
                }}>
                  Successful
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <Card sx={{
              borderRadius: 3,
              boxShadow: theme.palette.mode === 'dark' ? '0 8px 32px rgba(0,0,0,0.3)' : '0 8px 32px rgba(0,0,0,0.1)',
              background: theme.palette.mode === 'dark'
                ? 'linear-gradient(135deg, #0f766e 0%, #14b8a6 100%)'
                : 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
              color: 'white',
              position: 'relative',
              overflow: 'hidden',
              '&:hover': {
                transform: 'translateY(-5px)',
                boxShadow: theme.palette.mode === 'dark' ? '0 15px 45px rgba(0,0,0,0.4)' : '0 15px 45px rgba(0,0,0,0.2)',
              },
              transition: 'all 0.3s ease',
            }}>
              <Box sx={{
                position: 'absolute',
                top: -20,
                right: -20,
                width: 100,
                height: 100,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.1)',
                backdropFilter: 'blur(10px)',
              }} />
              <CardContent sx={{ p: { xs: 1, sm: 2 }, position: 'relative', zIndex: 1 }}>
                <Typography variant="h4" fontWeight={700} sx={{
                  mb: 1,
                  fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' }
                }}>
                  {stats.credits.toLocaleString()}
                </Typography>
                <Typography variant="body2" sx={{
                  opacity: 0.9,
                  fontWeight: 500,
                  fontSize: { xs: '0.7rem', sm: '0.8rem' }
                }}>
                  Credits
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <Card sx={{
              borderRadius: 3,
              boxShadow: theme.palette.mode === 'dark' ? '0 8px 32px rgba(0,0,0,0.3)' : '0 8px 32px rgba(0,0,0,0.1)',
              background: theme.palette.mode === 'dark'
                ? 'linear-gradient(135deg, #7c2d12 0%, #ea580c 100%)'
                : 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
              color: 'white',
              position: 'relative',
              overflow: 'hidden',
              '&:hover': {
                transform: 'translateY(-5px)',
                boxShadow: theme.palette.mode === 'dark' ? '0 15px 45px rgba(0,0,0,0.4)' : '0 15px 45px rgba(0,0,0,0.2)',
              },
              transition: 'all 0.3s ease',
            }}>
              <Box sx={{
                position: 'absolute',
                top: -20,
                right: -20,
                width: 100,
                height: 100,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.1)',
                backdropFilter: 'blur(10px)',
              }} />
              <CardContent sx={{ p: { xs: 1, sm: 2 }, position: 'relative', zIndex: 1 }}>
                <Typography variant="h4" fontWeight={700} sx={{
                  mb: 1,
                  fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' }
                }}>
                  {stats.debits.toLocaleString()}
                </Typography>
                <Typography variant="body2" sx={{
                  opacity: 0.9,
                  fontWeight: 500,
                  fontSize: { xs: '0.7rem', sm: '0.8rem' }
                }}>
                  Debits
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <Card sx={{
              borderRadius: 3,
              boxShadow: theme.palette.mode === 'dark' ? '0 8px 32px rgba(0,0,0,0.3)' : '0 8px 32px rgba(0,0,0,0.1)',
              background: theme.palette.mode === 'dark'
                ? 'linear-gradient(135deg, #581c87 0%, #a855f7 100%)'
                : 'linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)',
              color: 'white',
              position: 'relative',
              overflow: 'hidden',
              '&:hover': {
                transform: 'translateY(-5px)',
                boxShadow: theme.palette.mode === 'dark' ? '0 15px 45px rgba(0,0,0,0.4)' : '0 15px 45px rgba(0,0,0,0.2)',
              },
              transition: 'all 0.3s ease',
            }}>
              <Box sx={{
                position: 'absolute',
                top: -20,
                right: -20,
                width: 100,
                height: 100,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.1)',
                backdropFilter: 'blur(10px)',
              }} />
              <CardContent sx={{ p: { xs: 1, sm: 2 }, position: 'relative', zIndex: 1 }}>
                <Typography variant="h4" fontWeight={700} sx={{
                  mb: 1,
                  fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' }
                }}>
                  {transactions.filter(tx =>
                    tx.status?.toLowerCase() === 'reversal' ||
                    tx.purpose?.toLowerCase().includes('reversal')
                  ).length.toLocaleString()}
                </Typography>
                <Typography variant="body2" sx={{
                  opacity: 0.9,
                  fontWeight: 500,
                  fontSize: { xs: '0.7rem', sm: '0.8rem' }
                }}>
                  Reversals
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <Card sx={{
              borderRadius: 3,
              boxShadow: theme.palette.mode === 'dark' ? '0 8px 32px rgba(0,0,0,0.3)' : '0 8px 32px rgba(0,0,0,0.1)',
              background: theme.palette.mode === 'dark'
                ? 'linear-gradient(135deg, #991b1b 0%, #dc2626 100%)'
                : 'linear-gradient(135deg, #ff8c00 0%, #ff6347 100%)',
              color: 'white',
              position: 'relative',
              overflow: 'hidden',
              '&:hover': {
                transform: 'translateY(-5px)',
                boxShadow: theme.palette.mode === 'dark' ? '0 15px 45px rgba(0,0,0,0.4)' : '0 15px 45px rgba(0,0,0,0.2)',
              },
              transition: 'all 0.3s ease',
            }}>
              <Box sx={{
                position: 'absolute',
                top: -20,
                right: -20,
                width: 100,
                height: 100,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.1)',
                backdropFilter: 'blur(10px)',
              }} />
              <CardContent sx={{ p: { xs: 1, sm: 2 }, position: 'relative', zIndex: 1 }}>
                <Typography variant="h4" fontWeight={700} sx={{
                  mb: 1,
                  fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' }
                }}>
                  {transactions.filter(tx =>
                    tx.status?.toLowerCase() === 'failed'
                  ).length.toLocaleString()}
                </Typography>
                <Typography variant="body2" sx={{
                  opacity: 0.9,
                  fontWeight: 500,
                  fontSize: { xs: '0.7rem', sm: '0.8rem' }
                }}>
                  Failed
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} sm={6} md={3}>
            <Card sx={{
              borderRadius: 3,
              boxShadow: theme.palette.mode === 'dark' ? '0 8px 32px rgba(0,0,0,0.3)' : '0 8px 32px rgba(0,0,0,0.1)',
              background: theme.palette.mode === 'dark'
                ? 'linear-gradient(135deg, #065f46 0%, #047857 100%)'
                : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              color: 'white',
              position: 'relative',
              overflow: 'hidden',
              '&:hover': {
                transform: 'translateY(-5px)',
                boxShadow: theme.palette.mode === 'dark' ? '0 15px 45px rgba(0,0,0,0.4)' : '0 15px 45px rgba(0,0,0,0.2)',
              },
              transition: 'all 0.3s ease',
            }}>
              <Box sx={{
                position: 'absolute',
                top: -20,
                right: -20,
                width: 100,
                height: 100,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.1)',
                backdropFilter: 'blur(10px)',
              }} />
              <CardContent sx={{ p: { xs: 1, sm: 2 }, position: 'relative', zIndex: 1 }}>
                <Typography variant="h4" fontWeight={700} sx={{
                  mb: 1,
                  fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' }
                }}>
                  {formatAmount(transactions
                    .filter(tx => tx.trnxType?.toLowerCase() === 'credit' &&
                      (tx.purpose?.toLowerCase().includes('bank transfer') ||
                        tx.purpose?.toLowerCase().includes('internal transfer')))
                    .reduce((sum, tx) => sum + (Number(tx.amount) || 0), 0))}
                </Typography>
                <Typography variant="body2" sx={{
                  opacity: 0.9,
                  fontWeight: 500,
                  fontSize: { xs: '0.7rem', sm: '0.8rem' }
                }}>
                  Total Credit Amount
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <Card sx={{
              borderRadius: 3,
              boxShadow: theme.palette.mode === 'dark' ? '0 8px 32px rgba(0,0,0,0.3)' : '0 8px 32px rgba(0,0,0,0.1)',
              background: theme.palette.mode === 'dark'
                ? 'linear-gradient(135deg, #991b1b 0%, #dc2626 100%)'
                : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              color: 'white',
              position: 'relative',
              overflow: 'hidden',
              '&:hover': {
                transform: 'translateY(-5px)',
                boxShadow: theme.palette.mode === 'dark' ? '0 15px 45px rgba(0,0,0,0.4)' : '0 15px 45px rgba(0,0,0,0.2)',
              },
              transition: 'all 0.3s ease',
            }}>
              <Box sx={{
                position: 'absolute',
                top: -20,
                right: -20,
                width: 100,
                height: 100,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.1)',
                backdropFilter: 'blur(10px)',
              }} />
              <CardContent sx={{ p: { xs: 1, sm: 2 }, position: 'relative', zIndex: 1 }}>
                <Typography variant="h4" fontWeight={700} sx={{
                  mb: 1,
                  fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' }
                }}>
                  {formatAmount(transactions
                    .filter(tx => tx.trnxType?.toLowerCase() === 'debit')
                    .reduce((sum, tx) => sum + (Number(tx.amount) || 0), 0))}
                </Typography>
                <Typography variant="body2" sx={{
                  opacity: 0.9,
                  fontWeight: 500,
                  fontSize: { xs: '0.7rem', sm: '0.8rem' }
                }}>
                  Total Debit Amount
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Success Message */}
      {successMessage && (
        <Alert
          severity="success"
          sx={{ mb: 2, borderRadius: 2 }}
          onClose={() => setSuccessMessage('')}
        >
          <Typography variant="body1" fontWeight={600}>
            {successMessage}
          </Typography>
        </Alert>
      )}

      {/* Bulk Actions Bar */}
      {isSuperAdmin && selectedIds.length > 0 && (
        <Paper
          sx={{
            p: 2,
            mb: 2,
            backgroundColor: alpha(theme.palette.error.main, 0.1),
            border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
            borderRadius: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="body1" fontWeight={600} color="error.main">
              {selectedIds.length} transaction{selectedIds.length > 1 ? 's' : ''} selected
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteForeverIcon />}
                onClick={() => setDeleteDialogOpen(true)}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                }}
              >
                Delete Selected
              </Button>
              <Button
                variant="text"
                onClick={() => {
                  setSelectedIds([]);
                  setSelectAll(false);
                }}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                }}
              >
                Clear Selection
              </Button>
            </Box>
          </Box>
        </Paper>
      )}

      {/* Filters Section */}
      <Paper sx={{
        p: { xs: 1.5, sm: 2 },
        mb: { xs: 2, sm: 3 },
        borderRadius: 2,
        boxShadow: theme.palette.mode === 'dark'
          ? '0 4px 12px rgba(0,0,0,0.2)'
          : '0 4px 12px rgba(0,0,0,0.05)',
        background: theme.palette.mode === 'dark'
          ? 'rgba(30, 41, 59, 0.8)'
          : 'rgba(255, 255, 255, 0.8)',
        backdropFilter: 'blur(10px)',
        border: theme.palette.mode === 'dark'
          ? '1px solid rgba(255, 255, 255, 0.1)'
          : '1px solid rgba(255, 255, 255, 0.2)',
      }}>
        <Typography variant="h6" fontWeight={600} gutterBottom sx={{
          mb: 2,
          fontSize: { xs: '1rem', sm: '1.25rem' }
        }}>
          Filters & Search
        </Typography>

        <Grid container spacing={{ xs: 1, sm: 2 }} alignItems="center">
          {/* Search */}
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              placeholder="Search by ID, name, amount, description, purpose, status..."
              value={filter}
              onChange={e => { setFilter(e.target.value); setPage(1); }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: 'background.paper',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                  '&:hover': {
                    boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                  },
                  '&.Mui-focused': {
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  },
                },
              }}
            />
          </Grid>

          {/* Date Range */}
          <Grid item xs={6} sm={6} md={3}>
            <TextField
              fullWidth
              type="date"
              variant="outlined"
              size="small"
              label="Start Date"
              value={startDate}
              onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
              InputLabelProps={{ shrink: true }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />
          </Grid>

          <Grid item xs={6} sm={6} md={3}>
            <TextField
              fullWidth
              type="date"
              variant="outlined"
              size="small"
              label="End Date"
              value={endDate}
              onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
              InputLabelProps={{ shrink: true }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />
          </Grid>

          {/* Status Filter */}
          <Grid item xs={6} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                label="Status"
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="success">Success</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
                <MenuItem value="total failed">Total Failed</MenuItem>
                <MenuItem value="reversal">Reversal</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Type Filter */}
          <Grid item xs={6} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Type</InputLabel>
              <Select
                value={typeFilter}
                onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}
                label="Type"
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="all">All Types</MenuItem>
                <MenuItem value="credit">Credit</MenuItem>
                <MenuItem value="debit">Debit</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Purpose Filter */}
          <Grid item xs={6} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Purpose</InputLabel>
              <Select
                value={purposeFilter}
                onChange={(e) => { setPurposeFilter(e.target.value); setPage(1); }}
                label="Purpose"
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="all">All Purposes</MenuItem>
                <MenuItem value="transfer">Transfer</MenuItem>
                <MenuItem value="airtime">Airtime</MenuItem>
                <MenuItem value="data">Data</MenuItem>
                <MenuItem value="cable">Cable TV</MenuItem>
                <MenuItem value="electricity">Electricity</MenuItem>
                <MenuItem value="deposit">Deposit</MenuItem>
                <MenuItem value="withdrawal">Withdrawal</MenuItem>
                <MenuItem value="payment">Payment</MenuItem>
                <MenuItem value="refund">Refund</MenuItem>
                <MenuItem value="commission">Commission</MenuItem>
                <MenuItem value="bonus">Bonus</MenuItem>
                <MenuItem value="salary">Salary</MenuItem>
                <MenuItem value="funding">Funding</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Source Filter */}
          <Grid item xs={6} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Source</InputLabel>
              <Select
                value={sourceFilter}
                onChange={(e) => { setSourceFilter(e.target.value); setPage(1); }}
                label="Source"
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="all">All Sources</MenuItem>
                <MenuItem value="internal">Internal</MenuItem>
                <MenuItem value="paystack">Paystack</MenuItem>
                <MenuItem value="payscribe">Payscribe</MenuItem>
                <MenuItem value="safehaven">Safehaven</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Rows Per Page */}
          <Grid item xs={6} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Rows Per Page</InputLabel>
              <Select
                value={rowsPerPage}
                onChange={handleRowsPerPageChange}
                label="Rows Per Page"
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
                <MenuItem value={150}>150</MenuItem>
                <MenuItem value={200}>200</MenuItem>
                <MenuItem value={500}>500</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Clear Filters */}
          <Grid item xs={6} sm={6} md={2}>
            <Button
              fullWidth
              variant="outlined"
              onClick={clearFilters}
              size={isMobile ? "small" : "medium"}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                height: { xs: 36, sm: 40 },
                fontSize: { xs: '0.75rem', sm: '0.875rem' }
              }}
            >
              Clear Filters
            </Button>
          </Grid>
        </Grid>
      </Paper>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <LoadingSpinner size="large" />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ borderRadius: 3, boxShadow: '0 4px 15px rgba(0,0,0,0.1)' }}>{error}</Alert>
      ) : (
        <>
          <Paper
            sx={{
              mt: { xs: 1, sm: 2 },
              width: '100%',
              overflow: 'auto',
              borderRadius: 3,
              boxShadow: theme.palette.mode === 'dark'
                ? '0 8px 32px rgba(0,0,0,0.3)'
                : '0 8px 32px rgba(0,0,0,0.1)',
              background: theme.palette.mode === 'dark'
                ? 'rgba(30, 41, 59, 0.9)'
                : 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(10px)',
              border: theme.palette.mode === 'dark'
                ? '1px solid rgba(255, 255, 255, 0.1)'
                : '1px solid rgba(255, 255, 255, 0.2)',
              '& .MuiTableHead-root': {
                background: theme.palette.mode === 'dark'
                  ? 'linear-gradient(135deg, rgba(168, 10, 10, 0.1) 0%, rgba(255, 107, 107, 0.1) 100%)'
                  : 'linear-gradient(135deg, rgba(168, 10, 10, 0.05) 0%, rgba(255, 107, 107, 0.05) 100%)',
              },
              '& .MuiTableCell-head': {
                fontWeight: 800,
                color: theme.palette.mode === 'dark' ? '#ff6b6b' : '#A80A0A',
                borderBottom: `3px solid ${alpha(theme.palette.mode === 'dark' ? '#ff6b6b' : '#A80A0A', 0.2)}`,
                whiteSpace: 'nowrap',
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                padding: { xs: '8px 4px', sm: '16px' },
              },
              '& .MuiTableCell-body': {
                borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                padding: { xs: '8px 4px', sm: '16px' },
              },
              '& .MuiTableRow-root:hover': {
                background: theme.palette.mode === 'dark'
                  ? 'linear-gradient(135deg, rgba(168, 10, 10, 0.05) 0%, rgba(255, 107, 107, 0.05) 100%)'
                  : 'linear-gradient(135deg, rgba(168, 10, 10, 0.02) 0%, rgba(255, 107, 107, 0.02) 100%)',
                transform: 'scale(1.001)',
                boxShadow: theme.palette.mode === 'dark'
                  ? '0 4px 15px rgba(0,0,0,0.2)'
                  : '0 4px 15px rgba(0,0,0,0.05)',
              },
              '& .MuiTableRow-root': {
                transition: 'all 0.3s ease',
                '&:nth-of-type(even)': {
                  backgroundColor: theme.palette.mode === 'dark'
                    ? 'rgba(255,255,255,0.02)'
                    : 'rgba(0,0,0,0.02)',
                },
              },
            }}
          >
            <TableContainer sx={{
              minWidth: isMobile ? 800 : 0,
              maxWidth: '100%',
              overflowX: 'auto'
            }}>
              <Table size={isMobile ? 'small' : 'medium'}>
                <TableHead>
                  <TableRow>
                    {isSuperAdmin && (
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectAll}
                          indeterminate={selectedIds.length > 0 && selectedIds.length < paginatedTransactions.length}
                          onChange={handleSelectAll}
                          sx={{
                            color: theme.palette.primary.main,
                            '&.Mui-checked': {
                              color: theme.palette.primary.main,
                            },
                          }}
                        />
                      </TableCell>
                    )}
                    <TableCell>Reference ID</TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>UserName</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>Balance Before</TableCell>
                    <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>Balance After</TableCell>
                    <TableCell>Trnx Type</TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Purpose</TableCell>
                    <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>Source</TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Network</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Date</TableCell>
                    {isSuperAdmin && <TableCell>Actions</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedTransactions.map((tx) => {
                    const transactionId = tx._id || tx.id;
                    const isSelected = selectedIds.includes(transactionId);

                    return (
                      <TableRow
                        key={transactionId}
                        sx={{
                          '&:last-child td': { borderBottom: 0 },
                          transition: 'all 0.2s ease',
                          backgroundColor: isSelected ? alpha(theme.palette.primary.main, 0.05) : 'inherit',
                        }}
                      >
                        {isSuperAdmin && (
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={isSelected}
                              onChange={() => handleSelectTransaction(transactionId)}
                              sx={{
                                color: theme.palette.primary.main,
                                '&.Mui-checked': {
                                  color: theme.palette.primary.main,
                                },
                              }}
                            />
                          </TableCell>
                        )}
                        <TableCell>
                          <Typography
                            sx={{
                              cursor: 'pointer',
                              color: 'primary.main',
                              fontWeight: 600,
                              '&:hover': {
                                textDecoration: 'underline',
                              },
                            }}
                            onClick={() => handleTransactionClick(tx)}
                          >
                            {tx.reference || transactionId}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box>
                              <Typography
                                variant="body2"
                                fontWeight={600}
                                sx={{
                                  cursor: 'pointer',
                                  color: 'primary.main',
                                  '&:hover': {
                                    textDecoration: 'underline',
                                    color: 'primary.dark',
                                  },
                                }}
                                onClick={() => handleUserClick({
                                  _id: tx.userID,
                                  fullname: tx.userName || tx.user,
                                  username: tx.username,
                                  email: tx.userEmail
                                })}
                              >
                                {tx.userName || tx.user || ''}
                              </Typography>
                              {tx.username && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  display="block"
                                  sx={{
                                    cursor: 'pointer',
                                    '&:hover': {
                                      textDecoration: 'underline',
                                      color: 'primary.main',
                                    },
                                  }}
                                  onClick={() => handleUserClick({
                                    _id: tx.userID,
                                    fullname: tx.userName || tx.user,
                                    username: tx.username,
                                    email: tx.userEmail
                                  })}
                                >
                                  ({tx.username})
                                </Typography>
                              )}
                            </Box>
                            <UserActionMenu
                              user={{
                                _id: tx.userID,
                                username: tx.username,
                                email: tx.userEmail,
                                fullname: tx.userName || tx.user
                              }}
                            />
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600} color={tx.type?.toLowerCase() === 'debit' ? 'error.main' : 'success.main'}>
                            {tx.amount !== undefined ? `₦${Number(tx.amount).toLocaleString()}` : '-'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                          <Typography variant="body2" fontWeight={600}>
                            {tx.balanceBefore !== undefined ? `₦${Number(tx.balanceBefore).toLocaleString()}` : '-'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                          <Typography variant="body2" fontWeight={600}>
                            {tx.balanceAfter !== undefined ? `₦${Number(tx.balanceAfter).toLocaleString()}` : '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={tx.trnxType || '-'}
                            size="small"
                            sx={{
                              backgroundColor: tx.trnxType?.toLowerCase() === 'debit' ? 'error.main' : 'success.main',
                              color: '#fff',
                              fontWeight: 600,
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                          <Typography variant="body2" color="text.secondary">
                            {tx.purpose || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                          <Typography variant="body2" color="text.secondary">
                            {getTransactionSource(tx)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {tx.networkimg ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <img
                                src={`/images/${tx.networkimg.toLowerCase()}.png`}
                                alt={tx.networkimg}
                                style={{
                                  width: 32,
                                  height: 32,
                                  objectFit: 'contain',
                                  borderRadius: '4px',
                                }}
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.style.display = 'none';
                                }}
                              />
                              <Typography variant="body2" color="text.secondary">
                                {tx.network || '-'}
                              </Typography>
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              {tx.purpose || '-'}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={normalizeStatus(tx.status, getTransactionSource(tx))}
                            size="small"
                            sx={{
                              backgroundColor:
                                normalizeStatus(tx.status, getTransactionSource(tx)).toLowerCase() === 'success' ? 'success.main' :
                                  normalizeStatus(tx.status, getTransactionSource(tx)).toLowerCase() === 'failed' ? 'error.main' :
                                    'warning.main',
                              color: '#fff',
                              fontWeight: 600,
                              minWidth: 80,
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {tx.createdAt ? new Date(tx.createdAt).toLocaleString() : '-'}
                          </Typography>
                        </TableCell>
                        {isSuperAdmin && (
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                              {/* Approve Refund for SafeHaven reversal-pending */}
                              {getTransactionSource(tx) === 'safehaven' && (tx.status?.toLowerCase() === 'reversal_pending') && (
                                <Tooltip title="Approve refund to user">
                                  <span>
                                    <Button
                                      variant="contained"
                                      color="success"
                                      size={isMobile ? 'small' : 'medium'}
                                      onClick={() => handleApproveRefund(tx)}
                                      disabled={approveLoadingId === (tx._id || tx.id)}
                                      sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700 }}
                                    >
                                      {approveLoadingId === (tx._id || tx.id) ? 'Approving...' : 'Approve Refund'}
                                    </Button>
                                  </span>
                                </Tooltip>
                              )}
                              {/* Reversal button for pending transactions older than 5 minutes */}
                              {isEligibleForReversal(tx) && (
                                <Tooltip title="Reverse transaction (mark as failed)">
                                  <IconButton
                                    size="small"
                                    color="warning"
                                    onClick={() => handleReversalClick(tx)}
                                    sx={{
                                      '&:hover': {
                                        backgroundColor: alpha(theme.palette.warning.main, 0.1),
                                      },
                                    }}
                                  >
                                    <UndoIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}

                              {/* Delete button */}
                              <Tooltip title="Delete transaction">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleSingleDelete(transactionId)}
                                  sx={{
                                    '&:hover': {
                                      backgroundColor: alpha(theme.palette.error.main, 0.1),
                                    },
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
          <Box display="flex" justifyContent="space-between" alignItems="center" mt={4} mb={3} flexWrap="wrap" gap={3}>
            {/* Enhanced Summary Information */}
            <Box sx={{
              background: theme.palette.mode === 'dark'
                ? 'rgba(30, 41, 59, 0.9)'
                : 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(10px)',
              borderRadius: 3,
              p: 2,
              boxShadow: theme.palette.mode === 'dark'
                ? '0 4px 15px rgba(0,0,0,0.3)'
                : '0 4px 15px rgba(0,0,0,0.1)',
              border: theme.palette.mode === 'dark'
                ? '1px solid rgba(255, 255, 255, 0.1)'
                : '1px solid rgba(255, 255, 255, 0.2)',
              minWidth: '300px'
            }}>
              <Typography variant="body1" fontWeight={600} color="text.primary" sx={{ mb: 0.5 }}>
                Showing {((page - 1) * rowsPerPage) + 1} to {Math.min(page * rowsPerPage, totalTransactions)} of {totalTransactions.toLocaleString()} transactions
              </Typography>
              <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip
                  label={`Page ${page} of ${pageCount}`}
                  size="small"
                  sx={{
                    background: 'linear-gradient(135deg, #A80A0A 0%, #FF6B6B 100%)',
                    color: 'white',
                    fontWeight: 600,
                  }}
                />
                <Chip
                  label={`${rowsPerPage} per page`}
                  size="small"
                  variant="outlined"
                  sx={{
                    borderColor: '#A80A0A',
                    color: '#A80A0A',
                    fontWeight: 600,
                  }}
                />
              </Box>
            </Box>

            {/* Enhanced Pagination */}
            <Box sx={{
              background: theme.palette.mode === 'dark'
                ? 'rgba(30, 41, 59, 0.9)'
                : 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(10px)',
              borderRadius: 3,
              p: 2,
              boxShadow: theme.palette.mode === 'dark'
                ? '0 4px 15px rgba(0,0,0,0.3)'
                : '0 4px 15px rgba(0,0,0,0.1)',
              border: theme.palette.mode === 'dark'
                ? '1px solid rgba(255, 255, 255, 0.1)'
                : '1px solid rgba(255, 255, 255, 0.2)',
            }}>
              <Pagination
                count={pageCount}
                page={page}
                onChange={(_, value) => setPage(value)}
                color="primary"
                size="large"
                showFirstButton
                showLastButton
                sx={{
                  '& .MuiPaginationItem-root': {
                    borderRadius: 2,
                    fontWeight: 600,
                    fontSize: '1rem',
                    '&.Mui-selected': {
                      background: 'linear-gradient(135deg, #A80A0A 0%, #FF6B6B 100%)',
                      color: 'white',
                      boxShadow: '0 4px 15px rgba(168, 10, 10, 0.3)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #8B0000 0%, #E55A5A 100%)',
                      },
                    },
                    '&:hover': {
                      background: 'rgba(168, 10, 10, 0.1)',
                      transform: 'translateY(-1px)',
                    },
                    transition: 'all 0.3s ease',
                  },
                }}
              />
            </Box>
          </Box>
        </>
      )}

      {/* Enhanced Export Menu */}
      <Menu
        anchorEl={exportAnchorEl}
        open={Boolean(exportAnchorEl)}
        onClose={handleExportClose}
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
            minWidth: 250,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(15px)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            overflow: 'hidden',
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box sx={{
          background: 'linear-gradient(135deg, #A80A0A 0%, #FF6B6B 100%)',
          color: 'white',
          p: 2,
          mb: 1,
        }}>
          <Typography variant="h6" fontWeight={700}>
            Export Options
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9 }}>
            Choose your preferred format
          </Typography>
        </Box>

        <MenuItem
          onClick={exportToExcel}
          disabled={exportLoading}
          sx={{
            py: 2,
            px: 3,
            '&:hover': {
              background: 'rgba(33, 115, 70, 0.1)',
              transform: 'translateX(5px)',
            },
            transition: 'all 0.3s ease',
          }}
        >
          <TableChartIcon sx={{ mr: 2, color: '#217346', fontSize: 28 }} />
          <Box>
            <Typography variant="body1" fontWeight={600} color="#217346">
              Export to Excel (CSV)
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Download as spreadsheet format
            </Typography>
          </Box>
        </MenuItem>

        <MenuItem
          onClick={exportToPDF}
          disabled={exportLoading}
          sx={{
            py: 2,
            px: 3,
            '&:hover': {
              background: 'rgba(220, 53, 69, 0.1)',
              transform: 'translateX(5px)',
            },
            transition: 'all 0.3s ease',
          }}
        >
          <PictureAsPdfIcon sx={{ mr: 2, color: '#DC3545', fontSize: 28 }} />
          <Box>
            <Typography variant="body1" fontWeight={600} color="#DC3545">
              Export to PDF
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Download as PDF document
            </Typography>
          </Box>
        </MenuItem>
      </Menu>

      {/* Enhanced Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 12px 40px rgba(0,0,0,0.2)',
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(15px)',
          },
        }}
      >
        <DialogTitle sx={{
          color: 'error.main',
          fontWeight: 700,
          background: 'linear-gradient(135deg, rgba(220, 53, 69, 0.1) 0%, rgba(255, 107, 107, 0.1) 100%)',
          borderBottom: '1px solid rgba(220, 53, 69, 0.2)',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <DeleteForeverIcon sx={{ fontSize: 28, color: 'error.main' }} />
            Confirm Delete
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Are you sure you want to delete {selectedIds.length} selected transaction{selectedIds.length > 1 ? 's' : ''}?
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            This action cannot be undone. The transaction{selectedIds.length > 1 ? 's' : ''} will be permanently removed from the system.
          </Typography>
          <Alert severity="warning" sx={{ borderRadius: 2 }}>
            <Typography variant="body2" fontWeight={600}>
              ⚠️ This is a permanent action and cannot be reversed.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1, gap: 2 }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            variant="outlined"
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              borderColor: 'rgba(0,0,0,0.2)',
              '&:hover': {
                borderColor: 'rgba(0,0,0,0.4)',
                background: 'rgba(0,0,0,0.05)',
              },
            }}
          >
            Cancel
          </Button>
          {isSuperAdmin && (
            <Button
              onClick={handleDeleteSelected}
              variant="contained"
              color="error"
              disabled={deleteLoading}
              startIcon={deleteLoading ? <CircularProgress size={16} /> : <DeleteForeverIcon />}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                background: 'linear-gradient(135deg, #DC3545 0%, #FF6B6B 100%)',
                boxShadow: '0 4px 15px rgba(220, 53, 69, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #C82333 0%, #E55A5A 100%)',
                  boxShadow: '0 6px 20px rgba(220, 53, 69, 0.4)',
                  transform: 'translateY(-1px)',
                },
                '&:disabled': {
                  background: 'linear-gradient(135deg, #ccc 0%, #ddd 100%)',
                  transform: 'none',
                  boxShadow: 'none',
                },
                transition: 'all 0.3s ease',
              }}
            >
              {deleteLoading ? 'Deleting...' : 'Delete Permanently'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Reversal Confirmation Dialog */}
      <Dialog
        open={reversalDialogOpen}
        onClose={handleReversalCancel}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 12px 40px rgba(0,0,0,0.2)',
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(15px)',
          },
        }}
      >
        <DialogTitle sx={{
          color: 'warning.main',
          fontWeight: 700,
          background: 'linear-gradient(135deg, rgba(255, 193, 7, 0.1) 0%, rgba(255, 152, 0, 0.1) 100%)',
          borderBottom: '1px solid rgba(255, 193, 7, 0.2)',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <UndoIcon sx={{ fontSize: 28, color: 'warning.main' }} />
            Confirm Transaction Reversal
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Are you sure you want to reverse this transaction?
          </Typography>
          {selectedTransactionForReversal && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body1" color="text.secondary" gutterBottom>
                <strong>Transaction Details:</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • Reference: {selectedTransactionForReversal.reference || selectedTransactionForReversal._id}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • Amount: ₦{Number(selectedTransactionForReversal.amount).toLocaleString()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • User: {selectedTransactionForReversal.userName || selectedTransactionForReversal.user}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • Created: {selectedTransactionForReversal.createdAt ? new Date(selectedTransactionForReversal.createdAt).toLocaleString() : '-'}
              </Typography>
            </Box>
          )}
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            This will mark the transaction as failed and reverse any associated wallet changes. The transaction will remain in the system but with a "failed" status.
          </Typography>
          <Alert severity="warning" sx={{ borderRadius: 2 }}>
            <Typography variant="body2" fontWeight={600}>
              ⚠️ This action will reverse the transaction and mark it as failed. Please ensure this is the correct action.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1, gap: 2 }}>
          <Button
            onClick={handleReversalCancel}
            variant="outlined"
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              borderColor: 'rgba(0,0,0,0.2)',
              '&:hover': {
                borderColor: 'rgba(0,0,0,0.4)',
                background: 'rgba(0,0,0,0.05)',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleReversalConfirm}
            variant="contained"
            color="warning"
            disabled={reversalLoading}
            startIcon={reversalLoading ? <CircularProgress size={16} /> : <UndoIcon />}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              background: 'linear-gradient(135deg, #FFC107 0%, #FF9800 100%)',
              boxShadow: '0 4px 15px rgba(255, 193, 7, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #E0A800 0%, #F57C00 100%)',
                boxShadow: '0 6px 20px rgba(255, 193, 7, 0.4)',
                transform: 'translateY(-1px)',
              },
              '&:disabled': {
                background: 'linear-gradient(135deg, #ccc 0%, #ddd 100%)',
                transform: 'none',
                boxShadow: 'none',
              },
              transition: 'all 0.3s ease',
            }}
          >
            {reversalLoading ? 'Reversing...' : 'Reverse Transaction'}
          </Button>
        </DialogActions>
      </Dialog>

      <TransactionModal
        transaction={selectedTransaction}
        open={isModalOpen}
        onClose={handleCloseModal}
      />

      {/* User Modal */}
      <Dialog
        open={userDialogOpen}
        onClose={handleUserDialogClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            boxShadow: theme.palette.mode === 'dark'
              ? '0 20px 60px rgba(0,0,0,0.5)'
              : '0 20px 60px rgba(0,0,0,0.3)',
            background: theme.palette.mode === 'dark'
              ? 'linear-gradient(135deg, rgba(15, 23, 42, 0.95) 0%, rgba(30, 41, 59, 0.95) 100%)'
              : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
            backdropFilter: 'blur(20px)',
            border: theme.palette.mode === 'dark'
              ? '1px solid rgba(255, 255, 255, 0.1)'
              : '1px solid rgba(255, 255, 255, 0.2)',
            overflow: 'hidden',
          },
        }}
      >
        {selectedUser && (
          <>
            {/* Enhanced Header */}
            <DialogTitle sx={{
              background: 'linear-gradient(135deg, #A80A0A 0%, #FF6B6B 100%)',
              color: 'white',
              fontWeight: 700,
              p: 3,
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.1"%3E%3Ccircle cx="30" cy="30" r="2"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
                opacity: 0.3,
              }
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, position: 'relative', zIndex: 1 }}>
                {/* Enhanced Profile Picture */}
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    background: 'rgba(255, 255, 255, 0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    border: '3px solid rgba(255, 255, 255, 0.3)',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
                    position: 'relative',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      top: -2,
                      left: -2,
                      right: -2,
                      bottom: -2,
                      borderRadius: '50%',
                      background: 'linear-gradient(45deg, rgba(255,255,255,0.3), transparent)',
                      zIndex: -1,
                    }
                  }}
                >
                  {selectedUser.profilePic ? (
                    <img
                      src={selectedUser.profilePic}
                      alt={selectedUser.fullname || selectedUser.name || 'User'}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        borderRadius: '50%'
                      }}
                    />
                  ) : (
                    <Typography variant="h3" fontWeight={700} color="white">
                      {selectedUser.fullname?.[0] || selectedUser.name?.[0] || '?'}
                    </Typography>
                  )}
                </Box>

                {/* User Info */}
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h4" fontWeight={700} sx={{ mb: 1, textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
                    {selectedUser.fullname || selectedUser.name || 'User Details'}
                  </Typography>
                  <Typography variant="body1" sx={{ opacity: 0.9, mb: 1 }}>
                    {selectedUser.email}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Chip
                      label={selectedUser.status === 'active' ? 'Active' : 'Suspended'}
                      size="small"
                      sx={{
                        backgroundColor: selectedUser.status === 'active' ? 'rgba(34, 197, 94, 0.2)' : 'rgba(239, 68, 68, 0.2)',
                        color: selectedUser.status === 'active' ? '#22c55e' : '#ef4444',
                        fontWeight: 600,
                        border: '1px solid',
                        borderColor: selectedUser.status === 'active' ? 'rgba(34, 197, 94, 0.3)' : 'rgba(239, 68, 68, 0.3)',
                      }}
                    />
                    <Chip
                      label={selectedUser.role || 'User'}
                      size="small"
                      sx={{
                        backgroundColor: 'rgba(59, 130, 246, 0.2)',
                        color: '#3b82f6',
                        fontWeight: 600,
                        border: '1px solid rgba(59, 130, 246, 0.3)',
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            </DialogTitle>

            {/* Enhanced Content */}
            <DialogContent sx={{ p: 0 }}>
              {/* Balance Card */}
              <Box sx={{
                p: 3,
                background: theme.palette.mode === 'dark'
                  ? 'linear-gradient(135deg, #1e293b 0%, #334155 100%)'
                  : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                borderBottom: '1px solid rgba(0,0,0,0.1)',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '4px',
                  background: 'linear-gradient(90deg, #A80A0A 0%, #FF6B6B 100%)',
                }
              }}>
                <Typography variant="h6" fontWeight={600} color="text.secondary" gutterBottom>
                  Account Balance
                </Typography>
                <Typography
                  variant="h3"
                  fontWeight={800}
                  sx={{
                    color: Number(selectedUser.balance) > 10000 ? '#059669' :
                      Number(selectedUser.balance) >= 1000 ? '#d97706' : '#dc2626',
                    textShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    mb: 1
                  }}
                >
                  ₦{selectedUser.balance !== undefined ? Number(selectedUser.balance).toLocaleString() : '0'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Last updated: {selectedUser.updatedAt ? new Date(selectedUser.updatedAt).toLocaleString() : 'N/A'}
                </Typography>
              </Box>

              {/* Main Content Grid */}
              <Box sx={{ p: 3 }}>
                <Grid container spacing={3}>
                  {/* Personal Information */}
                  <Grid item xs={12} md={6}>
                    <Card sx={{
                      height: '100%',
                      background: theme.palette.mode === 'dark'
                        ? 'rgba(30, 41, 59, 0.8)'
                        : 'rgba(255, 255, 255, 0.8)',
                      backdropFilter: 'blur(10px)',
                      border: theme.palette.mode === 'dark'
                        ? '1px solid rgba(255,255,255,0.1)'
                        : '1px solid rgba(0,0,0,0.1)',
                      borderRadius: 3,
                      boxShadow: theme.palette.mode === 'dark'
                        ? '0 4px 20px rgba(0,0,0,0.3)'
                        : '0 4px 20px rgba(0,0,0,0.1)',
                    }}>
                      <CardContent sx={{ p: 3 }}>
                        <Typography variant="h6" fontWeight={700} color="primary.main" gutterBottom sx={{ mb: 2 }}>
                          Personal Information
                        </Typography>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <Box>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              Full Name
                            </Typography>
                            <Typography variant="body1" fontWeight={600}>
                              {selectedUser.fullname || selectedUser.name || '-'}
                            </Typography>
                          </Box>

                          <Box>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              Username
                            </Typography>
                            <Typography variant="body1" fontWeight={600}>
                              {selectedUser.username || '-'}
                            </Typography>
                          </Box>

                          <Box>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              Email Address
                            </Typography>
                            <Typography variant="body1" fontWeight={600}>
                              {selectedUser.email || '-'}
                            </Typography>
                          </Box>

                          <Box>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              Phone Number
                            </Typography>
                            <Typography variant="body1" fontWeight={600}>
                              {selectedUser.phone || '-'}
                            </Typography>
                          </Box>

                          <Box>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              Gender
                            </Typography>
                            <Typography variant="body1" fontWeight={600}>
                              {selectedUser.gender || '-'}
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Account Information */}
                  <Grid item xs={12} md={6}>
                    <Card sx={{
                      height: '100%',
                      background: theme.palette.mode === 'dark'
                        ? 'rgba(30, 41, 59, 0.8)'
                        : 'rgba(255, 255, 255, 0.8)',
                      backdropFilter: 'blur(10px)',
                      border: theme.palette.mode === 'dark'
                        ? '1px solid rgba(255,255,255,0.1)'
                        : '1px solid rgba(0,0,0,0.1)',
                      borderRadius: 3,
                      boxShadow: theme.palette.mode === 'dark'
                        ? '0 4px 20px rgba(0,0,0,0.3)'
                        : '0 4px 20px rgba(0,0,0,0.1)',
                    }}>
                      <CardContent sx={{ p: 3 }}>
                        <Typography variant="h6" fontWeight={700} color="primary.main" gutterBottom sx={{ mb: 2 }}>
                          Account Information
                        </Typography>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <Box>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              Account Type
                            </Typography>
                            <Chip
                              label={selectedUser.role || 'User'}
                              size="small"
                              sx={{
                                backgroundColor: selectedUser.role === 'admin' ? 'rgba(168, 10, 10, 0.1)' : 'rgba(59, 130, 246, 0.1)',
                                color: selectedUser.role === 'admin' ? '#A80A0A' : '#3b82f6',
                                fontWeight: 600,
                              }}
                            />
                          </Box>

                          <Box>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              Account Status
                            </Typography>
                            <Chip
                              label={selectedUser.status || 'Active'}
                              size="small"
                              sx={{
                                backgroundColor: selectedUser.status === 'active' ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                                color: selectedUser.status === 'active' ? '#22c55e' : '#ef4444',
                                fontWeight: 600,
                              }}
                            />
                          </Box>

                          <Box>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              Joined Date
                            </Typography>
                            <Typography variant="body1" fontWeight={600}>
                              {selectedUser.createdAt ? new Date(selectedUser.createdAt).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              }) : '-'}
                            </Typography>
                          </Box>

                          <Box>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              Last Active
                            </Typography>
                            <Typography variant="body1" fontWeight={600}>
                              {selectedUser.lastSeen ? new Date(selectedUser.lastSeen).toLocaleString() : 'N/A'}
                            </Typography>
                          </Box>

                          <Box>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              Online Status
                            </Typography>
                            <Chip
                              label={selectedUser.isOnline ? 'Online' : 'Offline'}
                              size="small"
                              sx={{
                                backgroundColor: selectedUser.isOnline ? 'rgba(34, 197, 94, 0.1)' : 'rgba(107, 114, 128, 0.1)',
                                color: selectedUser.isOnline ? '#22c55e' : '#6b7280',
                                fontWeight: 600,
                              }}
                            />
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Verification Status */}
                  <Grid item xs={12} md={6}>
                    <Card sx={{
                      background: theme.palette.mode === 'dark'
                        ? 'rgba(30, 41, 59, 0.8)'
                        : 'rgba(255, 255, 255, 0.8)',
                      backdropFilter: 'blur(10px)',
                      border: theme.palette.mode === 'dark'
                        ? '1px solid rgba(255,255,255,0.1)'
                        : '1px solid rgba(0,0,0,0.1)',
                      borderRadius: 3,
                      boxShadow: theme.palette.mode === 'dark'
                        ? '0 4px 20px rgba(0,0,0,0.3)'
                        : '0 4px 20px rgba(0,0,0,0.1)',
                    }}>
                      <CardContent sx={{ p: 3 }}>
                        <Typography variant="h6" fontWeight={700} color="primary.main" gutterBottom sx={{ mb: 2 }}>
                          Verification Status
                        </Typography>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body2" color="text.secondary">
                              KYC Verification
                            </Typography>
                            <Chip
                              label={selectedUser.isKycVerified ? 'Verified' : 'Not Verified'}
                              size="small"
                              color={selectedUser.isKycVerified ? 'success' : 'default'}
                              sx={{ fontWeight: 600 }}
                            />
                          </Box>

                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body2" color="text.secondary">
                              NIN Verification
                            </Typography>
                            <Chip
                              label={selectedUser.isKycninVerified ? 'Verified' : 'Not Verified'}
                              size="small"
                              color={selectedUser.isKycninVerified ? 'success' : 'default'}
                              sx={{ fontWeight: 600 }}
                            />
                          </Box>

                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body2" color="text.secondary">
                              BVN Verification
                            </Typography>
                            <Chip
                              label={selectedUser.isKycbvnVerified ? 'Verified' : 'Not Verified'}
                              size="small"
                              color={selectedUser.isKycbvnVerified ? 'success' : 'default'}
                              sx={{ fontWeight: 600 }}
                            />
                          </Box>

                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body2" color="text.secondary">
                              Account Verified
                            </Typography>
                            <Chip
                              label={selectedUser.isVerified ? 'Verified' : 'Not Verified'}
                              size="small"
                              color={selectedUser.isVerified ? 'success' : 'default'}
                              sx={{ fontWeight: 600 }}
                            />
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Identity Documents */}
                  <Grid item xs={12} md={6}>
                    <Card sx={{
                      background: theme.palette.mode === 'dark'
                        ? 'rgba(30, 41, 59, 0.8)'
                        : 'rgba(255, 255, 255, 0.8)',
                      backdropFilter: 'blur(10px)',
                      border: theme.palette.mode === 'dark'
                        ? '1px solid rgba(255,255,255,0.1)'
                        : '1px solid rgba(0,0,0,0.1)',
                      borderRadius: 3,
                      boxShadow: theme.palette.mode === 'dark'
                        ? '0 4px 20px rgba(0,0,0,0.3)'
                        : '0 4px 20px rgba(0,0,0,0.1)',
                    }}>
                      <CardContent sx={{ p: 3 }}>
                        <Typography variant="h6" fontWeight={700} color="primary.main" gutterBottom sx={{ mb: 2 }}>
                          Identity Documents
                        </Typography>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <Box>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              NIN Number
                            </Typography>
                            <Typography variant="body1" fontWeight={600} fontFamily="monospace">
                              {selectedUser.nin || selectedUser.vninNo || '-'}
                            </Typography>
                          </Box>

                          <Box>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              BVN Number
                            </Typography>
                            <Typography variant="body1" fontWeight={600} fontFamily="monospace">
                              {selectedUser.bvn || selectedUser.vbvnNo ?
                                (selectedUser.bvn || selectedUser.vbvnNo).slice(0, 2) + '*****' + (selectedUser.bvn || selectedUser.vbvnNo).slice(-4) :
                                '-'
                              }
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Virtual Account Details */}
                  {selectedUser.virtuaAccountBank && (
                    <Grid item xs={12}>
                      <Card sx={{
                        background: theme.palette.mode === 'dark'
                          ? 'linear-gradient(135deg, rgba(168, 10, 10, 0.1) 0%, rgba(255, 107, 107, 0.1) 100%)'
                          : 'linear-gradient(135deg, rgba(168, 10, 10, 0.05) 0%, rgba(255, 107, 107, 0.05) 100%)',
                        border: theme.palette.mode === 'dark'
                          ? '1px solid rgba(168, 10, 10, 0.3)'
                          : '1px solid rgba(168, 10, 10, 0.2)',
                        borderRadius: 3,
                        boxShadow: theme.palette.mode === 'dark'
                          ? '0 4px 20px rgba(0,0,0,0.3)'
                          : '0 4px 20px rgba(0,0,0,0.1)',
                      }}>
                        <CardContent sx={{ p: 3 }}>
                          <Typography variant="h6" fontWeight={700} color="primary.main" gutterBottom sx={{ mb: 2 }}>
                            Virtual Account Details
                          </Typography>

                          <Grid container spacing={3}>
                            <Grid item xs={12} sm={4}>
                              <Box>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                  Bank Name
                                </Typography>
                                <Typography variant="body1" fontWeight={600}>
                                  {selectedUser.virtuaAccountBank}
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                              <Box>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                  Account Name
                                </Typography>
                                <Typography variant="body1" fontWeight={600}>
                                  {selectedUser.virtuaAccountName}
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                              <Box>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                  Account Number
                                </Typography>
                                <Typography variant="body1" fontWeight={600} fontFamily="monospace">
                                  {selectedUser.virtuaAccountNo}
                                </Typography>
                              </Box>
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>
                    </Grid>
                  )}
                </Grid>
              </Box>
            </DialogContent>

            {/* Enhanced Actions */}
            <DialogActions sx={{
              p: 3,
              pt: 2,
              background: theme.palette.mode === 'dark'
                ? 'rgba(30, 41, 59, 0.8)'
                : 'rgba(248, 250, 252, 0.8)',
              borderTop: '1px solid rgba(0,0,0,0.1)',
              gap: 2
            }}>
              <Button
                onClick={handleUserDialogClose}
                variant="outlined"
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  borderColor: 'rgba(0,0,0,0.2)',
                  color: 'text.primary',
                  '&:hover': {
                    borderColor: 'rgba(0,0,0,0.4)',
                    background: 'rgba(0,0,0,0.05)',
                  },
                }}
              >
                Close
              </Button>
              <Button
                variant="contained"
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  background: 'linear-gradient(135deg, #A80A0A 0%, #FF6B6B 100%)',
                  boxShadow: '0 4px 15px rgba(168, 10, 10, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #8B0000 0%, #E55A5A 100%)',
                    boxShadow: '0 6px 20px rgba(168, 10, 10, 0.4)',
                    transform: 'translateY(-1px)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                View Transactions
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default AllTransactions;  